import axios from "@/config/axios";
import { marked } from "marked";

const ONE_HOUR_MS = 60 * 60 * 1000;

export default {
  name: "ChatWidget",
  data() {
    return {
      isOpen: false,
      newMessage: "",
      messages: [],
      isLoading: false,
      remaining: 10,
      maxLimit: 10,
      chatStartTime: null,
      chatLastTime: null,
      expireCheckTimer: null
    };
  },
  created() {
    this.loadMessages();
    try {
      const savedOpen = sessionStorage.getItem("loidoc_chat_open");
      if (savedOpen === "true") {
        this.isOpen = true;
        this.fetchQuota();
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    } catch (e) {
      console.warn("Could not read chat state from sessionStorage", e);
    }
  },
  mounted() {
    // Kiểm tra định kỳ mỗi 30s: nếu qua 1h thì tự động reset khung chat về ban đầu
    this.expireCheckTimer = setInterval(() => {
      this.checkExpiration();
    }, 30000);
  },
  beforeUnmount() {
    if (this.expireCheckTimer) {
      clearInterval(this.expireCheckTimer);
    }
  },
  methods: {
    checkExpiration() {
      if (!this.messages || this.messages.length === 0) return;
      const now = Date.now();
      if (this.chatLastTime && now - this.chatLastTime > ONE_HOUR_MS) {
        this.resetChat();
        this.fetchQuota();
      }
    },
    resetChat() {
      this.messages = [];
      this.chatStartTime = null;
      this.chatLastTime = null;
      try {
        localStorage.removeItem(this.getStorageKey());
        localStorage.removeItem("loidoc_chat_messages");
      } catch (e) {
        console.warn("Could not remove chat messages from localStorage", e);
      }
    },
    toggleChat() {
      this.isOpen = !this.isOpen;
      try {
        sessionStorage.setItem("loidoc_chat_open", this.isOpen ? "true" : "false");
      } catch (e) {}
      if (this.isOpen) {
        this.loadMessages();
        this.checkExpiration();
        this.fetchQuota();
        this.$nextTick(() => {
          this.$refs.messageInput?.focus();
          this.scrollToBottom();
        });
      }
    },
    getStorageKey() {
      try {
        const raw = localStorage.getItem("current-account");
        if (raw) {
          const acc = JSON.parse(raw);
          const uid = acc.id || acc.accountId || acc.username;
          if (uid) return `loidoc_chat_messages_${uid}`;
        }
      } catch (e) {}
      return "loidoc_chat_messages_guest";
    },
    saveMessages() {
      try {
        const key = this.getStorageKey();
        if (!this.messages || this.messages.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        const messagesToSave = this.messages.slice(-30);
        const now = Date.now();
        if (!this.chatStartTime) {
          this.chatStartTime = now;
        }
        this.chatLastTime = now;
        const payload = {
          messages: messagesToSave,
          startTime: this.chatStartTime,
          lastTime: this.chatLastTime
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        console.warn("Could not save chat messages to localStorage", e);
      }
    },
    loadMessages() {
      try {
        const key = this.getStorageKey();
        let data = localStorage.getItem(key);
        if (!data) {
          data = localStorage.getItem("loidoc_chat_messages");
        }
        if (data) {
          const parsed = JSON.parse(data);
          const now = Date.now();

          if (Array.isArray(parsed)) {
            this.messages = parsed;
            this.chatStartTime = now;
            this.chatLastTime = now;
          } else if (parsed && Array.isArray(parsed.messages)) {
            const lastTime = parsed.lastTime || 0;
            // Nếu đã quá 1 giờ kể từ tương tác cuối, reset khung chat về ban đầu
            if (now - lastTime > ONE_HOUR_MS) {
              this.resetChat();
              return;
            }
            this.messages = parsed.messages;
            this.chatStartTime = parsed.startTime || now;
            this.chatLastTime = parsed.lastTime || now;
          }
        }
      } catch (e) {
        console.warn("Could not load chat messages from localStorage", e);
      }
    },
    async fetchQuota() {
      try {
        const res = await axios.get("chat/v1/quota");
        if (res && res.data && typeof res.data.remaining === "number") {
          this.remaining = res.data.remaining;
          if (res.data.maxLimit) {
            this.maxLimit = res.data.maxLimit;
          }
          // Nếu số lượt đã đầy đủ và đã qua 1h kể từ khi bắt đầu chat -> reset khung chat
          if (
            this.remaining === this.maxLimit &&
            this.chatStartTime &&
            Date.now() - this.chatStartTime > ONE_HOUR_MS
          ) {
            this.resetChat();
          }
        }
      } catch (e) {
        console.warn("Could not fetch chat quota", e);
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim() || this.isLoading || this.remaining === 0) return;

      const userMessage = this.newMessage.trim();
      this.newMessage = "";

      // Reset height of textarea
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.style.height = "auto";
        }
      });

      const now = Date.now();
      if (!this.chatStartTime) {
        this.chatStartTime = now;
      }
      this.chatLastTime = now;

      // Add user message to UI
      this.messages.push({
        role: "user",
        content: userMessage
      });
      this.saveMessages();

      this.scrollToBottom();
      this.isLoading = true;

      try {
        const payload = {
          message: userMessage,
          history: this.messages.slice(0, -1)
        };

        const response = await axios.post("chat/v1/ask", payload);

        if (response && response.data) {
          if (typeof response.data.remaining === "number") {
            this.remaining = response.data.remaining;
          }

          this.chatLastTime = Date.now();
          this.messages.push({
            role: "model",
            content: response.data.response || "Xin lỗi, hiện tại tôi chưa tìm được thông tin phù hợp."
          });
          this.saveMessages();
        } else {
          this.chatLastTime = Date.now();
          this.messages.push({
            role: "model",
            content: "Xin lỗi, hiện tại tôi chưa tìm được thông tin phù hợp."
          });
          this.saveMessages();
        }
      } catch (error) {
        console.error("Chat Error:", error);
        this.chatLastTime = Date.now();
        if (error.response && error.response.status === 429) {
          this.remaining = 0;
          this.messages.push({
            role: "model",
            content: "Bạn đã dùng hết 10 lượt tư vấn trong 1 giờ. Vui lòng quay lại sau ít phút nhé!"
          });
          this.saveMessages();
        } else {
          this.messages.push({
            role: "model",
            content: "Đường truyền đang gián đoạn, bạn vui lòng thử lại sau nhé."
          });
          this.saveMessages();
        }
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
        this.$nextTick(() => {
          if (this.remaining > 0) {
            this.$refs.messageInput?.focus();
          }
        });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        setTimeout(() => {
          if (this.$refs.messagesContainer) {
            this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
          }
        }, 50);
      });
    },
    formatMessage(text) {
      if (!text) return "";
      return marked.parse(text);
    },
    handleLinkClick(event) {
      const link = event.target.closest("a");
      if (link) {
        const href = link.getAttribute("href");
        if (href && href.startsWith("/")) {
          event.preventDefault();
          this.$router.push(href);
        }
      }
    },
    autoResize() {
      const el = this.$refs.messageInput;
      if (el) {
        el.style.height = "auto";
        const newHeight = Math.min(el.scrollHeight, 100);
        el.style.height = newHeight + "px";
      }
    }
  }
};
